import React, { useState } from "react";
import { PlusIcon, TrashIcon, InformationCircleIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";

const DAY_PRESETS = [7, 3, 0];

/** Derive tier type from the data shape */
function getTierType(tier) {
  const hasTime = tier.daysBeforeEvent != null;
  const hasSeat = tier.seatThreshold != null;
  if (hasTime && hasSeat) return "hybrid";
  if (hasSeat) return "seats";
  return "schedule";
}

const TYPE_OPTIONS = [
  { value: "schedule", label: "Schedule" },
  { value: "seats", label: "Seats" },
  { value: "hybrid", label: "Hybrid" },
];

export function validatePriceTiers(tiers, baseCost = null) {
  const tierErrors = tiers.map(() => ({}));
  const globalWarnings = [];

  const dayGroups = {};
  const seatGroups = {};
  const priceGroups = {};

  tiers.forEach((tier, i) => {
    const type = getTierType(tier);

    if (tier.price == null || isNaN(tier.price) || tier.price < 0) {
      tierErrors[i].price = "Price must be 0 or more";
    } else {
      const key = String(tier.price);
      if (!priceGroups[key]) priceGroups[key] = [];
      priceGroups[key].push(i);
    }

    if (type === "schedule" || type === "hybrid") {
      const days = tier.daysBeforeEvent;
      if (days == null || isNaN(days) || days < 0) {
        tierErrors[i].days = "Must be 0 or more";
      } else {
        if (!dayGroups[days]) dayGroups[days] = [];
        dayGroups[days].push(i);
      }
    }

    if (type === "seats" || type === "hybrid") {
      const seats = tier.seatThreshold;
      if (seats == null || isNaN(seats) || seats <= 0) {
        tierErrors[i].seats = "Must be at least 1";
      } else {
        if (!seatGroups[seats]) seatGroups[seats] = [];
        seatGroups[seats].push(i);
      }
    }
  });

  Object.values(dayGroups).forEach((indices) => {
    if (indices.length > 1) {
      indices.forEach((i) => {
        tierErrors[i].days = "Duplicate — conflicts with another tier";
      });
    }
  });

  Object.values(seatGroups).forEach((indices) => {
    if (indices.length > 1) {
      indices.forEach((i) => {
        tierErrors[i].seats = "Duplicate — conflicts with another tier";
      });
    }
  });

  // Schedule/hybrid: prices should increase as event nears (lower daysBeforeEvent)
  const dayBasedTiers = tiers
    .map((t, i) => ({ ...t, _i: i }))
    .filter(
      (t) =>
        (getTierType(t) === "schedule" || getTierType(t) === "hybrid") &&
        t.daysBeforeEvent != null &&
        !isNaN(t.daysBeforeEvent) &&
        t.price != null &&
        !isNaN(t.price)
    )
    .sort((a, b) => b.daysBeforeEvent - a.daysBeforeEvent);

  if (dayBasedTiers.length > 1) {
    for (let i = 1; i < dayBasedTiers.length; i++) {
      if (dayBasedTiers[i].price < dayBasedTiers[i - 1].price) {
        globalWarnings.push(
          "Prices typically increase as the event gets closer — check your tier ordering."
        );
        break;
      }
    }
  }

  // Seats/hybrid: prices should increase as seats fill up (higher seatThreshold)
  const seatBasedTiers = tiers
    .map((t, i) => ({ ...t, _i: i }))
    .filter(
      (t) =>
        (getTierType(t) === "seats" || getTierType(t) === "hybrid") &&
        t.seatThreshold != null &&
        !isNaN(t.seatThreshold) &&
        t.price != null &&
        !isNaN(t.price)
    )
    .sort((a, b) => a.seatThreshold - b.seatThreshold);

  if (seatBasedTiers.length > 1) {
    for (let i = 1; i < seatBasedTiers.length; i++) {
      if (seatBasedTiers[i].price < seatBasedTiers[i - 1].price) {
        globalWarnings.push(
          "Prices typically increase as seats fill up — check your seat-tier ordering."
        );
        break;
      }
    }
  }

  // Duplicate prices across tiers
  Object.entries(priceGroups).forEach(([price, indices]) => {
    if (indices.length > 1) {
      const labels = indices.map((i) => i + 1).join(" and ");
      globalWarnings.push(
        `Tiers ${labels} have the same price ($${price}) — likely a mistake.`
      );
    }
  });

  const hasErrors = tierErrors.some((e) => Object.keys(e).length > 0);
  return { tierErrors, globalWarnings, hasErrors };
}

const FieldError = ({ message }) =>
  message ? <p className="text-xs text-red-500 dark:text-red-400 mt-0.5">{message}</p> : null;

const TierRow = ({ tier, originalIndex, onRemove, onTierChange, errors = {} }) => {
  const tierType = getTierType(tier);
  const hasAnyError = Object.keys(errors).length > 0;

  const handleTypeChange = (e) => {
    const newType = e.target.value;
    const updates = { ...tier };
    if (newType === "schedule") {
      updates.seatThreshold = null;
      if (updates.daysBeforeEvent == null) updates.daysBeforeEvent = 3;
    } else if (newType === "seats") {
      updates.daysBeforeEvent = null;
      if (updates.seatThreshold == null) updates.seatThreshold = 10;
    } else {
      // hybrid
      if (updates.daysBeforeEvent == null) updates.daysBeforeEvent = 3;
      if (updates.seatThreshold == null) updates.seatThreshold = 10;
    }
    onTierChange(originalIndex, updates);
  };

  const inputClass = (hasError) =>
    `rounded-md border-0 py-1.5 px-2 text-gray-900 dark:text-gray-100 dark:bg-gray-800 ring-1 ring-inset ${
      hasError ? "ring-red-400 dark:ring-red-400" : "ring-gray-300 dark:ring-gray-600"
    } focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm`;

  return (
    <div
      className={`rounded-lg bg-gray-50 dark:bg-gray-800 ring-1 ${
        hasAnyError ? "ring-red-300 dark:ring-red-800" : "ring-gray-200 dark:ring-gray-700"
      } px-3 py-2 space-y-2`}
    >
      {/* Header: type selector + delete */}
      <div className="flex items-center justify-between">
        <select
          value={tierType}
          onChange={handleTypeChange}
          className="min-w-[100px] rounded-md border-0 py-1 px-2 text-gray-700 dark:text-gray-200 dark:bg-gray-800 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
        >
          {TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => onRemove(originalIndex)}
          className="text-gray-400 dark:text-gray-500 hover:text-red-500 transition-colors"
        >
          <TrashIcon className="h-4 w-4" />
        </button>
      </div>

      {/* Conditions + price */}
      <div className="flex items-start gap-2">
        {/* Conditions */}
        {tierType === "hybrid" ? (
          <div className="flex flex-col gap-1">
            <div>
              <div className="flex items-center gap-1">
                <input
                  type="text"
                  inputMode="numeric"
                  className={`w-10 ${inputClass(!!errors.days)}`}
                  value={tier.daysBeforeEvent != null ? String(tier.daysBeforeEvent) : ""}
                  onChange={(e) =>
                    onTierChange(originalIndex, { ...tier, daysBeforeEvent: Number(e.target.value) })
                  }
                />
                <span className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">days before</span>
              </div>
              <FieldError message={errors.days} />
            </div>
            <span className="self-start px-1.5 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
              OR
            </span>
            <div>
              <div className="flex items-center gap-1">
                <span className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">after</span>
                <input
                  type="text"
                  inputMode="numeric"
                  className={`w-10 ${inputClass(!!errors.seats)}`}
                  value={tier.seatThreshold != null ? String(tier.seatThreshold) : ""}
                  onChange={(e) =>
                    onTierChange(originalIndex, { ...tier, seatThreshold: Number(e.target.value) })
                  }
                />
                <span className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">seats</span>
              </div>
              <FieldError message={errors.seats} />
            </div>
          </div>
        ) : (
          <>
            {tierType === "schedule" && (
              <div>
                <div className="flex items-center gap-1">
                  <input
                    type="text"
                    inputMode="numeric"
                    className={`w-10 ${inputClass(!!errors.days)}`}
                    value={tier.daysBeforeEvent != null ? String(tier.daysBeforeEvent) : ""}
                    onChange={(e) =>
                      onTierChange(originalIndex, { ...tier, daysBeforeEvent: Number(e.target.value) })
                    }
                  />
                  <span className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">days before</span>
                </div>
                <FieldError message={errors.days} />
              </div>
            )}
            {tierType === "seats" && (
              <div>
                <div className="flex items-center gap-1">
                  <span className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">after</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    className={`w-10 ${inputClass(!!errors.seats)}`}
                    value={tier.seatThreshold != null ? String(tier.seatThreshold) : ""}
                    onChange={(e) =>
                      onTierChange(originalIndex, { ...tier, seatThreshold: Number(e.target.value) })
                    }
                  />
                  <span className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">seats</span>
                </div>
                <FieldError message={errors.seats} />
              </div>
            )}
          </>
        )}

        <span className="text-gray-400 dark:text-gray-500 mt-[9px]">→</span>

        {/* Price */}
        <div>
          <div className="relative rounded-md shadow-sm w-16">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2">
              <span className="text-gray-500 dark:text-gray-400 sm:text-sm">$</span>
            </div>
            <input
              type="text"
              inputMode="decimal"
              className={`block w-full rounded-md border-0 py-1.5 pl-5 pr-2 text-gray-900 dark:text-gray-100 dark:bg-gray-800 ring-1 ring-inset ${
                errors.price ? "ring-red-400 dark:ring-red-400" : "ring-gray-300 dark:ring-gray-600"
              } focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm`}
              value={tier.price != null ? String(tier.price) : ""}
              onChange={(e) =>
                onTierChange(originalIndex, { ...tier, price: Number(e.target.value) })
              }
            />
          </div>
          <FieldError message={errors.price} />
        </div>
      </div>
    </div>
  );
};

const PriceScheduleInput = ({ value = [], onChange, baseCost = null }) => {
  const tiers = value;
  const [showInfo, setShowInfo] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const { tierErrors, globalWarnings, hasErrors } = validatePriceTiers(tiers, baseCost);

  const handleAdd = () => {
    const validPrices = tiers
      .map((t) => Number(t.price))
      .filter((p) => !isNaN(p) && p > 0);
    const base = Number(baseCost);
    const priceAnchor = validPrices.length > 0
      ? Math.max(...validPrices)
      : (!isNaN(base) ? base : 0);
    const nextPrice = priceAnchor + 5;

    const usedDays = tiers.map((t) => t.daysBeforeEvent).filter((d) => d != null);
    const nextDays = DAY_PRESETS.find((d) => !usedDays.includes(d)) ?? 1;

    const usedSeats = tiers.map((t) => t.seatThreshold).filter((s) => s != null);
    const nextSeats = usedSeats.length > 0 ? Math.max(...usedSeats) + 5 : 10;

    const lastType = tiers.length > 0 ? getTierType(tiers[tiers.length - 1]) : "schedule";

    let newTier;
    if (lastType === "seats") {
      newTier = { seatThreshold: nextSeats, price: nextPrice };
    } else if (lastType === "hybrid") {
      newTier = { daysBeforeEvent: nextDays, seatThreshold: nextSeats, price: nextPrice };
    } else {
      newTier = { daysBeforeEvent: nextDays, price: nextPrice };
    }

    onChange([...tiers, newTier]);
  };

  const handleRemove = (index) => {
    onChange(tiers.filter((_, i) => i !== index));
  };

  const handleTierChange = (index, updatedTier) => {
    onChange(tiers.map((tier, i) => (i !== index ? tier : updatedTier)));
  };

  // Sort for display: by daysBeforeEvent desc
  const sorted = [...tiers]
    .map((tier, originalIndex) => ({ tier, originalIndex }))
    .sort((a, b) => {
      const aDays = a.tier.daysBeforeEvent ?? -1;
      const bDays = b.tier.daysBeforeEvent ?? -1;
      return bDays - aDays;
    });

  return (
    <div>
      <div className="flex items-center gap-1 mb-1">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center gap-1 text-left focus:outline-none"
        >
          <svg
            className={`w-4 h-4 text-gray-400 dark:text-gray-500 transform transition-transform ${expanded ? "rotate-180" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
          <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 cursor-pointer">
            Pricing Tiers
            {tiers.length > 0 && (
              <span className="ml-1 text-gray-400 dark:text-gray-500 font-normal">
                ({tiers.length})
              </span>
            )}
          </label>
        </button>
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowInfo((v) => !v)}
            className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <InformationCircleIcon className="h-4 w-4" />
          </button>
          {showInfo && (
            <div className="absolute left-0 top-6 z-10 w-56 max-w-[calc(100vw-2rem)] rounded-md bg-white dark:bg-gray-800 shadow-lg ring-1 ring-gray-200 dark:ring-gray-700 p-3 text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
              <p>
                <strong className="font-medium text-gray-700 dark:text-gray-200">Schedule</strong> — triggers X
                days before the event.
              </p>
              <p className="mt-1">
                <strong className="font-medium text-gray-700 dark:text-gray-200">Seats</strong> — triggers when
                attendee count is reached.
              </p>
              <p className="mt-1">
                <strong className="font-medium text-gray-700 dark:text-gray-200">Hybrid</strong> — whichever
                condition comes first.
              </p>
            </div>
          )}
        </div>
        {!expanded && hasErrors && (
          <ExclamationTriangleIcon className="h-4 w-4 text-red-500 dark:text-red-400" />
        )}
      </div>
      {expanded && (
        <>
          {sorted.length > 0 && (
            <div className="space-y-2 mb-2">
              {sorted.map(({ tier, originalIndex }) => (
                <TierRow
                  key={originalIndex}
                  tier={tier}
                  originalIndex={originalIndex}
                  onRemove={handleRemove}
                  onTierChange={handleTierChange}
                  errors={tierErrors[originalIndex] || {}}
                />
              ))}
            </div>
          )}
          {globalWarnings.map((warning, i) => (
            <p key={i} className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400 mb-2">
              <ExclamationTriangleIcon className="h-3.5 w-3.5 flex-shrink-0" />
              {warning}
            </p>
          ))}
          <button
            type="button"
            onClick={handleAdd}
            className="flex items-center gap-1 px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <PlusIcon className="h-4 w-4" />
            Add Tier
          </button>
        </>
      )}
    </div>
  );
};

export default PriceScheduleInput;
